import { debounce } from 'lodash';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

const PaginatedBookSlider = dynamic(
  () => import('../components/BookComp/PaginatedBookSlider'),
);
const RecentlyViewedSlider = dynamic(
  () => import('../components/RecentlyViewed/RecentlyViewedSlider'),
);
const PaginatedCoursesSlider = dynamic(
  () => import('../components/Courses/PaginatedCoursesSlider'),
);
const PaginatedSchoolSlider = dynamic(
  () => import('../components/ResourcesPage/SchoolsPage/PaginatedSchoolSlider'),
);
const PaginatedOrangesBookSlider = dynamic(
  () => import('../components/BookComp/PaginatedOrangesBookSlider'),
);

const HeroSection = dynamic(
  () => import('../components/V2/HomePage/HeroSection'),
);
const CorporateTopStats = dynamic(
  () => import('@/components/Corporate/CorporateTopStats'),
  { ssr: false },
);
// const HomepageSlider = dynamic(
//   () => import('../components/V2/HomePage/HomepageSlider'),
// );

// import { Skeleton } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';

// import Image from 'next/image';
import PaginatedBundleSlider from '@/components/BundleComp/PaginatedBundleSlider';
import RenderIf from '@/components/Common/RenderIf';
import BookOfTheMonthCarousel from '@/components/HomePage/BookOfTheMonth/BookOfTheMonthCarousel';
import FindsUsOn from '@/components/HomePage/FindsUsOn';
import GetApp from '@/components/HomePage/GetApp';
import ShortVideo from '@/components/HomePage/ShortVideo';
import SubscriptionPricing from '@/components/HomePage/SubscriptionPricing';
import TestimonialSection from '@/components/HomePage/TestimonialSection';

import { countryAtom } from '@/atoms/countryState';
import { getAllBestsellerSpotlight } from '@/services/BestsellerSpotlight';
import { getAllShowcaseShortVideo } from '@/services/ShowcaseShortVideo';
import { IBestsellerSpotlight } from '@/utils/interfaces/BestsellerSpotlight';
import { IShowcaseShortVideo } from '@/utils/interfaces/ShowcaseShortVideo';

// import useHiddenCategories from '@/utils/useHiddenCategories';
import { tokenUpdate } from '../atoms/authModalControl';
import { user } from '../atoms/user';
import { ShouldRender } from '../components/shared/basic/ShouldRender';
import { formateErrorObject } from '../components/shared/formateErrorObject';
import { getLoginToken } from '../components/shared/getToken';
import { notify } from '../components/shared/notify';
import { EventDetails, logEvent } from '../hooks/useClickEventTracking';
import { Endpoints } from '../network';
import {
  getAllBookCategories,
  getAllOnBoardingCategories,
  getAllShortBookListApi,
} from '../services/Library/Books';
import { getAllProducts } from '../services/Products';
import { BundleData } from '../utils/interfaces/Products/bundles';
import { User } from '../utils/interfaces/User';

export const generateUrlForImageBanner = (
  baseUrl: string,
  page: string,
  type: string,
): string => {
  const url = `${baseUrl}api/banner/images/${page}${type}`;
  return url;
};

const Home: React.FC = () => {
  // const [shortBookLoading, setShortBookLoading] = useState(false);
  const [, setShortBookLoading] = useState(false);
  const router = useRouter();
  const { view } = router.query;

  const [searchKeyword, setSearchKeyword] = useState<string>();
  const keyWord = router.query.search;
  const [, setBundleLoading] = useState(false);
  const [search, setSearch] = useState<string>();
  const userData: User | undefined = useRecoilValue(user);

  const [token, setToken] = useState('');
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const [, setBundleData] = useState<BundleData[]>([]);
  const [categories, setCategories] = useState<string>();
  const screens = useBreakpoint();
  const [bestsellerSpotlights, setBestsellerSpotlights] = useState<
    IBestsellerSpotlight[]
  >([]);
  const [showcaseShortVideos, setShowcaseShortVideos] = useState<
    IShowcaseShortVideo[]
  >([]);
  // const [shortBooks, setShortBooks] = useState<
  //   {
  //     _id: string;
  //     coverImageUrl: string;
  //     title: string;
  //     externalLink: string;
  //     slug: string;
  //     type: string;
  //     isListed: boolean;
  //     totalPages: number;
  //     isExternalLink: boolean;
  //   }[]
  // >([]);
  const [, setShortBooks] = useState<
    {
      _id: string;
      coverImageUrl: string;
      title: string;
      externalLink: string;
      slug: string;
      type: string;
      isListed: boolean;
      totalPages: number;
      isExternalLink: boolean;
    }[]
  >([]);
  // const hiddenCategories = useHiddenCategories();
  const countryState = useRecoilValue(countryAtom);
  const isIndia = countryState?.selectedCountry
    ? countryState.selectedCountry.code === 'IN'
    : true;


  const fetchData = async () => {
    try {
      const token = await getLoginToken();
      setToken(token as string);
      if (token) {
        handleGetCategories();
      }
    } catch (error) {
      console.error('Error fetching token:', error);
    }
  };



  const generateUrlForImage = (
    baseUrl: string,
    endpoint: string,
    id: string,
  ): string => {
    const encodedId = encodeURIComponent(id);
    const url = `${baseUrl}${endpoint}${encodedId}`;
    return url;
  };

  const handleGetCategories = async () => {
    try {
      const response = await getAllBookCategories();

      const allOnBoardingCategories = await getAllOnBoardingCategories('4');
      const { data } = response;

      setCategories(
        (data ?? response)
          .filter((category: any) => category.bookCount >= 1)
          .filter((category: any) =>
            allOnBoardingCategories.categories.includes(category?.name),
          )
          .map((category: any) => category?._id)
          .join(','),
      );
    } catch (error: any) {
      notify(formateErrorObject(error.message), 'error');
    }
  };

  const handleBundleDetails = async (title?: string) => {
    try {
      setBundleLoading(true);

      const data = await getAllProducts(1, 7, undefined, title, 20);

      const formattedData = formatBundleData(data?.data);
      if (formattedData.length === 0) {
        setBundleData([]);
        return;
      }

      setBundleData((prevData) => {
        const uniqueData = prevData?.filter(
          (prevItem) =>
            !formattedData.some(
              (newItem) => newItem.hardCopyId === prevItem.hardCopyId,
            ),
        );
        return [...uniqueData, ...formattedData];
      });
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setBundleLoading(false);
    }
  };

  const formatBundleData = (rawData: any[]): BundleData[] => {
    return rawData.map((bundle) => ({
      image: bundle?.thumbnail_image || '',
      softCopyId: bundle?.digital_pair?.id || '',
      hardCopyId: bundle?.id || '',
      heading: bundle?.name || '',
      slug: bundle?.slug || '',
      softCopyHeading: bundle?.digital_pair?.name || 0,
      desc: bundle?.description || '',
      currency: bundle?.currency_symbol || '',
      actualPriceHardCopy: bundle?.stroked_price || 0,
      actualPriceSoftCopy:
        (bundle?.digital_pair && bundle.digital_pair?.stroked_price) || 0,
      hardCopyPrice: bundle?.main_price || 0,
      softCopyPrice:
        (bundle?.digital_pair && bundle?.digital_pair.main_price) || 0,
      type: bundle?.category || '',
      external_link: bundle?.external_link || '',
      products: bundle?.bundle_products,
    }));
  };

  const handleGetAllShortBooks = async (title?: string) => {
    try {
      setShortBookLoading(true);
      const response = await getAllShortBookListApi(title);
      const { data } = response;
      const formattedBooks = data
        ?.map((book: any) => ({
          _id: book?._id,
          coverImageUrl: generateUrlForImage(
            `${process.env.NEXT_PUBLIC_API_READER_URL}/`,
            `${Endpoints.getCoverImagesForBooks}/`,
            `${book?._id}`,
          ),
          title: book?.title,
          slug: book?.slug,
          type: book?.type,
          totalPages: book?.totalPages,
          isListed: book.isListed,
          isExternalLink: book?.isExternalLink,
        }))
        .filter(
          (book: any) => book.type === 'short' && book.isListed !== false,
        );

      setShortBooks(formattedBooks);
    } catch (error: any) {
      notify(error, 'error');
    } finally {
      setShortBookLoading(false);
    }
  };

  const handleSearch = async (keyword: string) => {
    if (keyword !== undefined) {
      setSearch(keyword);
      handleGetAllShortBooks(keyword);
      handleBundleDetails(keyword);
    } else {
      handleGetAllShortBooks();
      handleBundleDetails();
    }
  };

  const delayedSearch = debounce((keyword: string) => {
    handleSearch(keyword);
    if (keyWord) {
      const eventParams: EventDetails = {
        event_name: 'search_keyword',
        source: 'global',
        source_type: 'search',
        source_id: null,
        sub_source: null,
        sub_source_id: null,
        unit: 'string',
        value: keyword,
      };

      logEvent({
        eventDetails: eventParams,
        userData,
        pathname: router.pathname,
      });
    }
  }, 500);

  useEffect(() => {
    delayedSearch(searchKeyword as string);
    return () => {
      delayedSearch.cancel();
      setSearch(undefined);
    };
  }, [searchKeyword]);

  useEffect(() => {
    fetchData();

    if (isTokenChanged) {
      handleGetAllShortBooks();
      handleBundleDetails();
      setIsTokenChanged(false);
    }
  }, [isTokenChanged]);

  useEffect(() => {
    setSearchKeyword(keyWord as string);
  }, [keyWord]);

  // const countBooksAndCourses = (products: any[]) => {
  //   let bookCount = 0;
  //   let courseCount = 0;

  //   products?.forEach((product: any) => {
  //     if (product?.category === 'Book') {
  //       bookCount++;
  //     } else if (product.category === 'Recorded Course') {
  //       courseCount++;
  //     }
  //   });

  //   return {
  //     bookCount,
  //     courseCount,
  //   };
  // };

  const getAllBestsellerSpotlightHandle = async () => {
    try {
      const result = await getAllBestsellerSpotlight();
      setBestsellerSpotlights(result.data);
    } catch (error) {
      console.error(error);
    }
  };

  const getAllShowcaseShortVideoHandle = async () => {
    try {
      const result = await getAllShowcaseShortVideo();
      setShowcaseShortVideos(result.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getAllShowcaseShortVideoHandle();
  }, []);

  useEffect(() => {
    getAllBestsellerSpotlightHandle();
  }, []);

  return (
    <>
      <Head>
        <title>
          Books Publisher in India for Finance, Marketing and more. | ZebraLearn
        </title>
        <meta
          name='description'
          content='Discover best books publisher in India specializing in non-fiction books. ZebraLearn offers cutting-edge design & technology insights, simplifying complex topics.
          '
          key='description'
        />
      </Head>

      <div className='flex flex-col'>
        <HeroSection />
        {/* <Banners banners={banners} /> */}

        {/* Corporate Metrics Section */}
        {userData?.organization?.role === 'admin' && (
          <div className='px-3 lg:px-6 xl:px-12 mt-4'>
            <CorporateTopStats hideIfNoData />
          </div>
        )}

        <div className='flex flex-col md:mt-4 pb-0 md:pb-0'>
          <RecentlyViewedSlider
            categoryName='Recently Viewed'
            search={search}
          />
          <PaginatedBookSlider
            categoryId={null}
            categoryName='Top 10 Books of all time'
            subHeadingCol={
              screens.md
                ? 'These books are our bestsellers of all time. Customers loved these books.'
                : 'These books are our bestsellers of all time.'
            }
            isTopTen={true}
            hideShortBook={view?.toString() === 'cred'}
          />
          <RenderIf condition={isIndia}>
            <SubscriptionPricing />
          </RenderIf>
          <ShouldRender check={categories}>
            <PaginatedBookSlider
              categoryId={null}
              categoryName='Recommended Books for you'
              subHeadingCol={
                screens.md
                  ? 'Books Chosen to Match Your Unique Reading Preferences'
                  : ''
              }
              search={search}
              order='asc'
              includeCategories={categories}
              hideShortBook={view?.toString() === 'cred'}
            />
          </ShouldRender>
          <RenderIf
            condition={
              bestsellerSpotlights && bestsellerSpotlights?.length
                ? true
                : false
            }
          >
            <BookOfTheMonthCarousel
              bestsellerSpotlights={bestsellerSpotlights}
            />
          </RenderIf>
          <ShouldRender check={view?.toString() !== 'cred'}>
            <PaginatedBookSlider
              categoryId={null}
              categoryName='Free Books for you'
              subHeading='Explore All'
              subHeadingLink='/books/shop/free-only/'
              search={search}
              order='asc'
              isHomePage={true}
              bookType='short'
              hideShortBook={view?.toString() === 'cred'}
            />
          </ShouldRender>
          <RenderIf
            condition={
              showcaseShortVideos && showcaseShortVideos?.length ? true : false
            }
          >
            <ShortVideo showcaseShortVideos={showcaseShortVideos} />
          </RenderIf>
          <PaginatedBookSlider
            categoryId={null}
            categoryName='Featured Books'
            search={search}
            isHomePage={true}
            order='desc'
            featured={true}
          />
          <PaginatedBundleSlider
            isAddToCart={false}
            categoryId={null}
            categoryName='Explore Curated Learning Bundles'
            search={search}
            isHomePage={true}
            order='desc'
          />
          <RenderIf condition={isIndia}>
            <PaginatedOrangesBookSlider
              categoryId={null}
              categoryName='Books'
              search={search}
              isHomePage={true}
              bookType='paperBack'
              hideShortBook={view?.toString() === 'cred'}
              hideBars={true}
              hidePaginationBars={true}
            />
          </RenderIf>
          <div className=''>
            <PaginatedCoursesSlider
              categoryId=''
              search={search}
              token={token}
              name='best courses from industry leaders'
            />
          </div>
          <PaginatedBookSlider
            categoryId={null}
            categoryName='Read Zebralearn Bestsellers'
            search={search}
            isHomePage={true}
            order='desc'
            hideShortBook={true}
            categoryIdFilter={
              process.env.NEXT_PUBLIC_APP_ENV === 'staging'
                ? '66d95bde2da439ae0a02b8b0'
                : '66c6db9e104ce84c7e2a8956'
            }
          />
          <div className=''>
            <PaginatedSchoolSlider
              categoryId={null}
              search={search}
              categoryName='Start with Schools, your daily reads'
            />
          </div>
          <FindsUsOn />
          <TestimonialSection />
          <GetApp />
          {/* <ShouldRender check={categories}>
            <PaginatedBookSlider
              categoryId={null}
              categoryName='Discover Your Favorites'
              search={search}
              order='asc'
              includeCategories={categories}
            />
          </ShouldRender>
          <RecentlyViewedSlider
            categoryName='Recently Viewed'
            search={search}
          />
          <PaginatedBookSlider
            categoryId={null}
            categoryName='Featured Books'
            search={search}
            isHomePage={true}
            order='desc'
            featured={true}
          />

          <PaginatedBookSlider
            categoryId={null}
            categoryName='Read Zebralearn Bestsellers'
            search={search}
            isHomePage={true}
            order='asc'
            hideShortBook={true}
            categoryIdFilter={
              process.env.NEXT_PUBLIC_APP_ENV === 'staging'
                ? '66d95bde2da439ae0a02b8b0'
                : '66c6db9e104ce84c7e2a8956'
            }
          />

          <ShouldRender check={!hiddenCategories.includes('mathematics')}>
            <PaginatedBookSlider
              categoryId={
                process.env.NEXT_PUBLIC_APP_ENV === 'staging'
                  ? '66d95bde2da439ae0a02b8b0'
                  : '66c6db9e104ce84c7e2a8956'
              }
              categoryName='CBSE Books For Class 6 - 9'
              search={search}
              isHomePage={true}
            />
          </ShouldRender>

          <PaginatedBookSlider
            categoryId={
              process.env.NEXT_PUBLIC_APP_ENV === 'staging'
                ? '6634d23a5c4cf495a9c52276'
                : '6630ef424d8718482ab83add'
            }
            categoryName='Top Career Books'
            search={search}
            isHomePage={true}
          />

          <div className=''>
            <ShouldRender check={shortBooks?.length || shortBookLoading}>
              <PaginatedBookSlider
                categoryId={null}
                categoryName='Quick Books to Level up'
                subHeading='Explore All'
                subHeadingLink='/books/shop/free-only/'
                search={search}
                order='asc'
                isHomePage={true}
                bookType='short'
              />
            </ShouldRender>
          </div>
          <PaginatedBookSlider
            categoryId={null}
            categoryName='Shelf loading soon: Magic in making'
            bookType='comingSoon'
            search={search}
            isHomePage={true}
          />

          <div className=''>
            <PaginatedSchoolSlider
              categoryId={null}
              search={search}
              categoryName='Start with Schools, your daily reads'
            />
          </div>

          <div className=''>
            <PaginatedCoursesSlider
              categoryId=''
              search={search}
              token={token}
              name='best courses from industry leaders'
            />
          </div>

          <ShouldRender check={bundleData?.length || bundleLoading}>
            <div className='mt-2'>
              <HomepageSlider
                loading={!!bundleData?.length}
                heading='Explore Curated Learning Bundles'
              >
                <span className='flex '>
                  {!bundleLoading
                    ? bundleData?.map((item, index) => {
                        return (
                          <span
                            key={`${item?.heading}-${index}`}
                            onClick={() => {
                              if (window !== undefined && item?.external_link) {
                                window.open(item?.external_link, '_blank');
                              }
                            }}
                            className='flex flex-col gap-y-4  no-select p-1 md:p-1.5  cursor-pointer'
                          >
                            <Image
                              src={item.image}
                              alt={item.heading}
                              width={430}
                              height={280}
                              className='rounded-md transition-transform transform hover:scale-[1.03] duration-500 min-w-[170px] lg:min-w-[220px] max-w-[220px] lg:max-w-[320px] h-[220px] lg:h-[320px]'
                              style={{ objectFit: 'cover' }}
                            />
                            <div>
                              <span className='flex text-base text-textGrayColor'>
                                <ShouldRender
                                  check={
                                    countBooksAndCourses(item?.products)
                                      .bookCount
                                  }
                                >
                                  {
                                    countBooksAndCourses(item?.products)
                                      .bookCount
                                  }{' '}
                                  Book{' '}
                                </ShouldRender>
                                <ShouldRender
                                  check={
                                    countBooksAndCourses(item?.products)
                                      .courseCount
                                  }
                                >
                                  +{' '}
                                  {
                                    countBooksAndCourses(item?.products)
                                      .courseCount
                                  }{' '}
                                  Course
                                </ShouldRender>
                              </span>

                              <span className='text-white text-sm xl:text-base font-medium'>
                                {item.heading}
                              </span>
                            </div>
                          </span>
                        );
                      })
                    : Array.from({ length: 6 }).map((_, index) => (
                        <span
                          key={index}
                          className='group pb-10 cursor-pointer p-1 md:p-1.5 bg-transparent no-select '
                        >
                          <Skeleton.Button
                            style={{
                              width: screens.lg ? '430px' : '200px',
                              height: screens.xl
                                ? '280px'
                                : screens.lg
                                  ? '200px'
                                  : '150px',
                            }}
                            active={true}
                            className='rounded-sm md:rounded-md transition-transform transform hover:scale-[1.03] duration-500 min-w-[170px] lg:min-w-[220px] max-h-[220px] lg:max-h-[320px]'
                          />
                        </span>
                      ))}
                </span>
              </HomepageSlider>
            </div>
          </ShouldRender> */}
        </div>
      </div>
    </>
  );
};

export default Home;
